import type { ReactNode } from 'react'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import AccountAccessTrustIndicatorsMapper from 'components/Account/AccountAccess/AccountAccessTrustIndicatorsMapper'
import LineGlassMartiniAltIcon from 'LuxKit/Icons/line/LineGlassMartiniAltIcon'
import LineGlobeAltIcon from 'LuxKit/Icons/line/LineGlobeAltIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import AccountAccessUserInput from 'components/Account/AccountAccess/AccountAccessUserInput'
import AccountAccessLegalText from 'components/Account/AccountAccess/AccountAccessLegalText'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import SocialButtons from 'components/Account/AccountAccess/AccountAccessSocialButtons'
import TextButton from 'LuxKit/Button/TextButton'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import useAccountAccessLoginStart from 'components/Account/AccountAccess/useAccountAccessLoginStart'
import LoginCaptcha from 'components/Account/AccountAccess/LoginCaptcha'
import ModalContext from 'contexts/ModalContext'
import { clearAuthError } from 'actions/AuthActions'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import LoadingIndicator from './Loading/LoadingIndicator'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import type { LastUsedLoginMethod } from 'lib/auth/lastUsedLoginMethod'
import useQueryParams from 'hooks/useQueryParams'

const Container = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(24)};
  margin-bottom: ${rem(20)};

  ${mediaQueryUp.tablet} {
    grid-template-columns: 1fr 1fr;
    margin-bottom: ${rem(40)};
  }

  &.flash-offer {
    grid-template-areas: 'intro' 'details' 'form';
    margin-bottom: 0;

    ${mediaQueryUp.tablet} {
      grid-template-areas: 'intro details' 'form details';
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      align-items: start;
      column-gap: ${rem(48)};
    }
  }
`

const Intro = styled(VerticalSpacer)`
  .flash-offer & {
    grid-area: intro;
  }
`

const Details = styled.div`
  grid-area: details;
  min-width: 0;
`

const Or = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(12)};

  &::after,
  &::before {
    content: '';
    flex: 1;
    border-top: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
    display: block;
  }
`

const InputGroup = styled.div`
  padding-block-end: ${rem(24)};

  .flash-offer & {
    padding-block-end: 0;
  }
`

const StyledVerticalSpacer = styled(VerticalSpacer)`
  position: relative;

  .flash-offer & {
    grid-area: form;
  }
`

const ModalSocialButtons = styled(SocialButtons)`
  && {
    margin-bottom: 0;
  }

  &.inline {
    flex-wrap: wrap;
    column-gap: ${rem(16)};
    row-gap: ${rem(16)};

    > * {
      flex: 1 1 calc(50% - ${rem(8)});
    }

    > * + * {
      margin-inline-start: 0;
    }

    ${mediaQueryUp.tablet} {
      > * {
        flex-basis: 0;
      }
    }
  }
`

interface Props {
  offerType: string
  saveUpToPercent?: number
  /** Offer details are shown in the flash offer treatment on either surface. */
  flashOfferDetails?: ReactNode
  /** Supplied only by the modal after mounting; walls do not show login history. */
  lastUsedLoginMethod?: LastUsedLoginMethod
  /** In a modal, continue through its existing account access steps. */
  onAuthStep?: (mode: App.UiAccountModalMode, userId: string, appleData?: App.AppleLoginData) => void
}

function OfferPageSignUpContent({ offerType, saveUpToPercent, flashOfferDetails, lastUsedLoginMethod, onAuthStep }: Props) {
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const showModal = useContext(ModalContext)
  const query = useQueryParams()
  const isPhoneLoginMethod = query.get('login_method') === 'phone'
  const loginMethod = isPhoneLoginMethod ? 'phone' : undefined
  const [applePrivateEmailData, setApplePrivateEmailData] = useState<App.AppleLoginData | null>(null)
  const error = useAppSelector(state => state.auth.error)

  const continueToAuthStep = useCallback((mode: App.UiAccountModalMode, nextUserId: string, appleData?: App.AppleLoginData) => {
    if (onAuthStep) {
      onAuthStep(mode, nextUserId, appleData)
      return
    }

    showModal(<AccountAccessModal
      initialMode={mode}
      defaultEmail={nextUserId}
      defaultAppleData={appleData}
    />)
  }, [onAuthStep, showModal])

  const { processing, isCheckingEmail, rejectionMessage, reRef, onCheckEmail, onPrivateAppleEmailLogin } = useAccountAccessLoginStart({
    onContinue: continueToAuthStep,
    applePrivateEmailData,
    setApplePrivateEmailData,
    trackEvents: !!onAuthStep,
  })
  const unlockAction = useMemo(() => <TextButton
    kind="primary"
    size="large"
    type="submit"
    fit={flashOfferDetails ? 'full-width' : undefined}
    disabled={isCheckingEmail}
  >
    {intl.formatMessage({
      id: '5mihMRbW39p0LPj_s1YC',
      defaultMessage: 'Unlock this offer',
      description: 'Submit button text to sign up and unlock a travel offer',
    })}
  </TextButton>, [intl, isCheckingEmail, flashOfferDetails])

  const isStoreMode = useAppSelector(state => state.system.storeMode)
  const socialMediaLoginsEnabled = config.FACEBOOK_APP_ID || config.GOOGLE_APP_ID || config.APPLE_APP_ID
  const showSocialMediaButtons = !isStoreMode && socialMediaLoginsEnabled

  useEffect(() => {
    dispatch(clearAuthError())
  }, [dispatch])

  return (
    <Container className={flashOfferDetails ? 'flash-offer' : undefined}>
      <Intro gap={flashOfferDetails ? 12 : 20}>
        <Group direction="vertical" gap={flashOfferDetails ? 12 : 8}>
          <HeadingTextBlock variant="heading2">
            {!!flashOfferDetails && !!saveUpToPercent && saveUpToPercent > 0 && <FormattedMessage
              id="hH-N__KWYqQPoK_h4jhq"
              defaultMessage="Sign up and <em>save up to {percent}%</em> on your stay"
              description="Headline of the flash hotel sign-up modal showing the current offer saving percentage"
              values={{ em: (chunks) => <em>{chunks}</em>, percent: saveUpToPercent }}
            />}
            {!flashOfferDetails && saveUpToPercent && saveUpToPercent > 0 && <FormattedMessage
              id="sEBzSPh2MXnRmZkjRTp6"
              defaultMessage="Sign up and <em>save</em> up to {percent}% on this {offerType}"
              description="Heading encouraging sign-up with discount percentage for specific offer type"
              values={{
                em: (chunks) => <em>{chunks}</em>,
                percent: saveUpToPercent,
                offerType,
              }}
            />}
            {!saveUpToPercent && <FormattedMessage
              id="NIHmLKcMOIoqQMkJzHRP"
              defaultMessage="Sign up for free to <em>unlock</em> this {offerType}"
              description="Heading encouraging free sign-up to unlock specific offer type"
              values={{
                em: (chunks) => <em>{chunks}</em>,
                offerType,
              }}
            />}
          </HeadingTextBlock>
          {!flashOfferDetails && <BodyTextBlock variant="medium">
            {intl.formatMessage({
              id: 'iFYSyODSC42IQWJR_ku0',
              defaultMessage: 'Join over 15 million global members to unlock our amazing deals & benefits!',
              description: 'Subtitle encouraging users to join the membership for exclusive travel deals',
            })}
          </BodyTextBlock>}
          {flashOfferDetails && <CSSBreakpoint min="tablet">
            <BodyTextBlock variant="large">
              {intl.formatMessage({
                id: '5wxxQ2mrsdJAcru2-Plx',
                defaultMessage: 'Join over 15 million global members for exclusive offers with extra inclusions and points on every booking.',
                description: 'Supporting text next to the offer card in the flash hotel sign-up modal',
              })}
            </BodyTextBlock>
          </CSSBreakpoint>}
        </Group>
        {!flashOfferDetails && <VerticalSpacer gap={4}>
          <BodyTextBlock variant="medium" startIcon={<LineGlassMartiniAltIcon colour="highlight-primary-dark" />}>
            <FormattedMessage
              id="4ZLNL6nUAaEGZKnEmC6S"
              defaultMessage="<b>More inclusions:</b> Drinks, dining, transfers & more"
              description="Feature highlight about additional travel inclusions"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LineGlobeAltIcon colour="highlight-primary-dark" />}>
            <FormattedMessage
              id="DDGC_bia1DpLWeDWRSUo"
              defaultMessage="<b>More options:</b> 1,000s of hotels, tours & flights"
              description="Feature highlight about variety of travel options available"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          </BodyTextBlock>
          <BodyTextBlock variant="medium" startIcon={<LinePhoneIcon colour="highlight-primary-dark" />}>
            <FormattedMessage
              id="ErPwcXtJOKpc9sTyFjno"
              defaultMessage="<b>More expertise:</b> 24/7 customer service, worldwide"
              description="Feature highlight about round-the-clock customer support"
              values={{ b: (chunks) => <b>{chunks}</b> }}
            />
          </BodyTextBlock>
        </VerticalSpacer>}
        {!flashOfferDetails && <Group direction="vertical" horizontalAlign="start">
          <AccountAccessTrustIndicatorsMapper />
        </Group>}
        {flashOfferDetails && <CSSBreakpoint min="tablet"><AccountAccessTrustIndicatorsMapper spread /></CSSBreakpoint>}
      </Intro>
      {flashOfferDetails && <Details>{flashOfferDetails}</Details>}
      <StyledVerticalSpacer gap={flashOfferDetails ? 16 : 20} as="form" onSubmit={onCheckEmail}>
        {error?.message && <MessageBanner kind="critical">{error.message}</MessageBanner>}
        {!error?.message && rejectionMessage && <MessageBanner kind="critical">{rejectionMessage}</MessageBanner>}
        <InputGroup>
          <AccountAccessUserInput
            action={flashOfferDetails ? undefined : unlockAction}
            loginMethod={flashOfferDetails ? loginMethod : undefined}
            showLastUsedTag={!!flashOfferDetails && !loginMethod && (lastUsedLoginMethod === 'email' || lastUsedLoginMethod === 'phone' || lastUsedLoginMethod === 'otp')}
            noValidationSpacing
            autoFocus={false}
          />
          <LoadingIndicator
            visible={!!processing || isCheckingEmail}
            floating
            backdrop="white"
          />
        </InputGroup>
        <LoginCaptcha turnstileAction="account_lookup" ref={reRef} />
        {flashOfferDetails && unlockAction}
        {showSocialMediaButtons && (!flashOfferDetails || (!isPhoneLoginMethod && !query.has('hide_sso'))) && (
          <>
            <Or>
              <BodyText colour="light-foreground-alt" variant="medium">
                {flashOfferDetails ? intl.formatMessage({
                  id: 'KhvccualqfIxGnzRO2Pt',
                  defaultMessage: 'or',
                  description: 'Divider between email entry and social login buttons in the flash offer sign-up modal',
                }) : intl.formatMessage({
                  id: 'zavm4LJuBiH9snGYYmpM',
                  defaultMessage: 'Other ways to log in',
                  description: 'Divider text above social login buttons on sign-up page',
                })}
              </BodyText>
            </Or>
            {flashOfferDetails ? <ModalSocialButtons
              mode="login"
              inline
              onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
              lastUsedLoginMethod={lastUsedLoginMethod}
            /> : <SocialButtons
              mode="login"
              inline
              onPrivateAppleEmailLogin={onPrivateAppleEmailLogin}
            />}
          </>
        )}
        {flashOfferDetails && <CSSBreakpoint max="mobile"><AccountAccessTrustIndicatorsMapper compact /></CSSBreakpoint>}
        <AccountAccessLegalText colour={flashOfferDetails ? 'neutral-three' : undefined} />
      </StyledVerticalSpacer>
    </Container>
  )
}

export default OfferPageSignUpContent
